import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Shell } from '../../components/shell/Shell.tsx';
import { Card } from '@/components/ui';
import { apiFetch } from '@/lib/api-client';

interface QuotaData { available: number; total: number }
interface AttemptRow {
  attempt_id: string;
  candidate_name: string;
  candidate_email: string;
  test_title: string;
  status: string;
  score: number;
  completed_at: string | null;
}
interface TeamReportData {
  by_test: Array<{ test_id: string; title: string; attempt_count: number; avg_score: number; pass_rate: number }>;
}
interface AssignedTestsData {
  items: Array<{ id: string; title: string }>;
}

export function CompanyDashboardPage(): JSX.Element {
  const params = useParams<{ id: string }>();
  const companyId = params.id ?? '';

  const [quota, setQuota] = useState<QuotaData | null>(null);
  const [attempts, setAttempts] = useState<AttemptRow[] | null>(null);
  const [report, setReport] = useState<TeamReportData | null>(null);
  const [assigned, setAssigned] = useState<AssignedTestsData | null>(null);

  const [quotaError, setQuotaError] = useState<string | null>(null);
  const [attemptsError, setAttemptsError] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [assignedError, setAssignedError] = useState<string | null>(null);

  useEffect(() => {
    const common = { credentials: 'include' as const };

    apiFetch(`/api/companies/${companyId}/quota`, common)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load quota');
        const data = (await res.json()) as { ok: boolean; data: QuotaData };
        if (!data.ok) throw new Error('Failed to load quota');
        setQuota(data.data);
      })
      .catch((err: unknown) => setQuotaError(err instanceof Error ? err.message : 'Failed to load quota'));

    apiFetch(`/api/companies/${companyId}/attempts?limit=10`, common)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load attempts');
        const data = (await res.json()) as { ok: boolean; data: { items: AttemptRow[] } };
        if (!data.ok) throw new Error('Failed to load attempts');
        setAttempts(data.data.items.slice(0, 10));
      })
      .catch((err: unknown) => setAttemptsError(err instanceof Error ? err.message : 'Failed to load attempts'));

    apiFetch(`/api/companies/${companyId}/team-report`, common)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load team report');
        const data = (await res.json()) as { ok: boolean; data: TeamReportData };
        if (!data.ok) throw new Error('Failed to load team report');
        setReport(data.data);
      })
      .catch((err: unknown) => setReportError(err instanceof Error ? err.message : 'Failed to load team report'));

    apiFetch(`/api/companies/${companyId}/assigned-tests?limit=5`, common)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load assigned tests');
        const data = (await res.json()) as { ok: boolean; data: AssignedTestsData };
        if (!data.ok) throw new Error('Failed to load assigned tests');
        setAssigned(data.data);
      })
      .catch((err: unknown) => setAssignedError(err instanceof Error ? err.message : 'Failed to load assigned tests'));
  }, [companyId]);

  const quotaUsed = useMemo(() => {
    if (!quota) return 0;
    return Math.max(0, quota.total - quota.available);
  }, [quota]);

  const quotaPercent = useMemo(() => {
    if (!quota || quota.total === 0) return 0;
    return Math.round((quotaUsed / quota.total) * 100);
  }, [quota, quotaUsed]);

  const aggregateMetrics = useMemo(() => {
    if (!report || report.by_test.length === 0) {
      return { totalAttempts: 0, passRatePercent: 0, avgScorePercent: 0 };
    }

    const totalAttempts = report.by_test.reduce((sum, test) => sum + test.attempt_count, 0);
    if (totalAttempts === 0) {
      return { totalAttempts: 0, passRatePercent: 0, avgScorePercent: 0 };
    }

    const passedWeighted = report.by_test.reduce(
      (sum, test) => sum + (test.pass_rate * test.attempt_count),
      0,
    );
    const avgScoreWeighted = report.by_test.reduce(
      (sum, test) => sum + (test.avg_score * test.attempt_count),
      0,
    );

    return {
      totalAttempts,
      passRatePercent: (passedWeighted / totalAttempts) * 100,
      avgScorePercent: avgScoreWeighted / totalAttempts,
    };
  }, [report]);

  const quotaColor = quotaPercent >= 100 ? 'bg-error' : quotaPercent >= 80 ? 'bg-warning' : 'bg-success';

  return (
    <Shell mode="company" densityMode="workspace" companyId={companyId}>
      <header className="mb-6">
        <p className="font-mono text-mono text-muted-2">COMPANY · DASHBOARD</p>
        <h1 className="mt-2 font-sans text-title font-medium">Company Dashboard</h1>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card variant="default">
            <h2 className="font-sans text-body font-medium">Quota usage</h2>
            {quotaError ? <p className="mt-2 text-small text-error">{quotaError}</p> : null}
            {!quota && !quotaError ? <p className="mt-2 text-small text-muted">Loading quota…</p> : null}
            {quota ? (
              <>
                <p className="mt-2 font-sans text-h3 font-medium">{quotaUsed} of {quota.total} attempts used</p>
                <div className="mt-3 h-3 w-full overflow-hidden rounded-pill bg-bg-warm">
                  <div
                    role="progressbar"
                    aria-label="Quota usage"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={quotaPercent}
                    className={`h-full ${quotaColor}`}
                    style={{ width: `${quotaPercent}%` }}
                  />
                </div>
              </>
            ) : null}
          </Card>

          <Card variant="default">
            <h2 className="font-sans text-body font-medium">At-a-glance metrics</h2>
            {reportError ? <p className="mt-2 text-small text-error">{reportError}</p> : null}
            {!report && !reportError ? <p className="mt-2 text-small text-muted">Loading metrics…</p> : null}
            {report ? (
              <dl className="mt-3 grid grid-cols-3 gap-3">
                <div><dt className="text-small text-muted">Total attempts</dt><dd className="font-medium">{aggregateMetrics.totalAttempts}</dd></div>
                <div><dt className="text-small text-muted">Pass rate</dt><dd className="font-medium">{aggregateMetrics.passRatePercent.toFixed(0)}%</dd></div>
                <div><dt className="text-small text-muted">Avg score</dt><dd className="font-medium">{aggregateMetrics.avgScorePercent.toFixed(1)}%</dd></div>
              </dl>
            ) : null}
          </Card>

          <Card variant="default" className="lg:col-span-2">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-sans text-body font-medium">Recent attempts</h2>
              <Link className="font-sans text-small font-medium text-accent" to={`/company/${companyId}/attempts`}>View all attempts</Link>
            </div>
            {attemptsError ? <p className="text-small text-error">{attemptsError}</p> : null}
            {!attempts && !attemptsError ? <p className="text-small text-muted">Loading attempts…</p> : null}
            {attempts ? (
              <table aria-label="Recent attempts" className="w-full text-left">
                <thead>
                  <tr className="border-b border-line"><th>Candidate</th><th>Test</th><th>Status</th><th>Score</th><th>Date</th></tr>
                </thead>
                <tbody>
                  {attempts.map((row) => (
                    <tr key={row.attempt_id} className="border-b border-line">
                      <td>{row.candidate_name}<div className="text-xs text-muted">{row.candidate_email}</div></td>
                      <td>{row.test_title}</td>
                      <td>{row.status}</td>
                      <td>{row.score.toFixed(1)}%</td>
                      <td>{row.completed_at ? new Date(row.completed_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </Card>

          <Card variant="default">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-sans text-body font-medium">Per-test summary</h2>
              <Link className="font-sans text-small font-medium text-accent" to={`/company/${companyId}/team-report`}>View team report</Link>
            </div>
            {reportError ? <p className="text-small text-error">{reportError}</p> : null}
            {!report && !reportError ? <p className="text-small text-muted">Loading team report…</p> : null}
            {report ? (
              <table aria-label="Per-test summary" className="w-full text-left">
                <thead><tr className="border-b border-line"><th>Test</th><th>Attempts</th><th>Avg score</th></tr></thead>
                <tbody>
                  {report.by_test.map((row) => (
                    <tr key={row.test_id} className="border-b border-line"><td>{row.title}</td><td>{row.attempt_count}</td><td>{row.avg_score.toFixed(1)}%</td></tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </Card>

          <Card variant="default">
            <h2 className="font-sans text-body font-medium">Assigned tests</h2>
            {assignedError ? <p className="mt-2 text-small text-error">{assignedError}</p> : null}
            {!assigned && !assignedError ? <p className="mt-2 text-small text-muted">Loading assigned tests…</p> : null}
            {assigned ? (
              <ul className="mt-3 space-y-2">
                {assigned.items.map((item) => <li key={item.id}>{item.title}</li>)}
              </ul>
            ) : null}
            <Link className="mt-4 inline-block font-sans text-small font-medium text-accent" to={`/company/${companyId}/share-tokens`}>Issue share tokens</Link>
          </Card>
      </div>
    </Shell>
  );
}
